import React, { useState, useEffect, useContext } from 'react';
import { getErrorMessage } from '../utils/errorHandler';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CreditCard, Calendar, MapPin, Euro, ArrowRight, AlertCircle } from 'lucide-react';

export default function ActivityPaymentPage() {
  const { activityId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activity, setActivity] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Get registration_id from state or query params
  const registrationId = location.state?.registrationId || new URLSearchParams(location.search).get('registration_id');

  useEffect(() => {
    if (!user) {
      toast.error('Devi effettuare il login per procedere al pagamento');
      navigate(`/login?redirect=/payment/activity/${activityId}${registrationId ? `?registration_id=${registrationId}` : ''}`);
      return;
    }
    
    if (!registrationId) {
      toast.error('ID registrazione mancante');
      navigate('/activities');
      return;
    }
    
    loadActivityAndRegistration();
  }, [activityId, registrationId, user]);

  const loadActivityAndRegistration = async () => {
    try {
      // Load activity details
      const activitiesResponse = await axios.get('/activities');
      const activityData = activitiesResponse.data.find(a => a.id === activityId);
      
      if (!activityData) {
        toast.error('Evento non trovato');
        navigate('/activities');
        return;
      }
      
      setActivity(activityData);

      if (!activityData.price || Number(activityData.price) <= 0) {
        toast.info('Questa attività non richiede pagamento');
        navigate('/activities');
        return;
      }

      // Non usare l'endpoint registrazioni admin-only: per l'utente basta avere il registration_id,
      // la validazione finale avviene su create-checkout-session-activity.
      setRegistration({
        id: registrationId,
        payment_status: 'pending'
      });
    } catch (error) {
      console.error('Failed to load activity/registration', error);
      toast.error('Errore nel caricamento dei dati');
      navigate('/activities');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      // Create Stripe Checkout Session
      const response = await axios.post('/payments/create-checkout-session-activity', null, {
        params: {
          registration_id: registrationId
        }
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('Checkout URL not received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(getErrorMessage(error, 'Errore nella creazione della sessione di pagamento'));
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!activity || !registration) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-20 px-4 pb-12 max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Pagamento Iscrizione</h1>
          <p className="text-gray-600">Completa il pagamento per confermare la tua iscrizione</p>
        </div>

        {/* Activity Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">{activity.title}</CardTitle>
            {activity.description && (
              <CardDescription className="text-base mt-2">{activity.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activity.start_datetime && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span>{new Date(activity.start_datetime).toLocaleDateString('it-IT', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              )}
              
              {activity.location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{activity.location}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="h-5 w-5" />
              Riepilogo Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Iscrizione Evento</span>
                <span className="font-semibold">€ {parseFloat(activity.price).toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center py-3 text-lg font-bold">
                <span>Totale</span>
                <span className="text-2xl">€ {parseFloat(activity.price).toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Info */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Pagamento Sicuro con Stripe</p>
                <p>Sarai reindirizzato alla pagina di pagamento sicura di Stripe per completare la transazione con carta di credito/debito.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/activities')}
            disabled={processing}
            className="flex-1"
          >
            Annulla
          </Button>
          <Button
            onClick={handlePayment}
            disabled={processing}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {processing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Elaborazione...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Procedi al Pagamento
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Security Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>🔒 Pagamento sicuro elaborato da Stripe</p>
          <p className="mt-1">I tuoi dati di pagamento sono protetti e non vengono memorizzati sui nostri server</p>
        </div>
      </div>
    </div>
  );
}
